import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { DIRECTORIES, TIER_LABELS } from "../lib/directories";
import { API } from "../lib/api";

async function J(url, opts = {}, key) {
  const r = await fetch(`${API}${url}`, {
    ...opts,
    headers: { "X-Admin-Key": key, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
}

const STATUS_META = {
  todo:      { label: "To do",     dot: "#C4C4C4", bg: "bg-rw-bg3 text-rw-ink2" },
  submitted: { label: "Submitted", dot: "#F0D48A", bg: "bg-yellow-100 text-yellow-800" },
  verified:  { label: "Verified",  dot: "#16a34a", bg: "bg-green-100 text-green-800" },
  rejected:  { label: "Rejected",  dot: "#dc2626", bg: "bg-red-100 text-red-800" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.todo;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-2.5 py-1 ${meta.bg}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
      {meta.label}
    </span>
  );
}

export default function DirectoryBlastTab({ adminKey }) {
  const [statuses, setStatuses] = useState({});
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await J("/admin/directories", {}, adminKey);
      setStatuses(d.statuses || {});
      setKit(d.listing_kit || null);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (slug, patch) => {
    try {
      const res = await J(`/admin/directories/${slug}`, { method: "PATCH", body: JSON.stringify(patch) }, adminKey);
      setStatuses((s) => ({ ...s, [slug]: res.directory }));
    } catch (e) { toast.error(e.message); }
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${label}`); }
    catch { toast.error("Couldn't copy"); }
  };

  const copyAll = () => {
    if (!kit) return;
    const block = [
      `Business name: ${kit.business_name}`,
      `Phone: ${kit.phone}`,
      `Address: ${kit.address_line}`,
      `City: ${kit.city}`,
      `Website: ${kit.website}`,
      `Categories: ${kit.categories.join(", ")}`,
      `Hours: ${kit.hours}`,
      ``,
      `Short description:`,
      kit.short_description,
      ``,
      `Long description:`,
      kit.long_description,
    ].join("\n");
    copy(block, "full listing kit");
  };

  const groups = TIER_LABELS.map((t) => ({ tier: t, items: DIRECTORIES.filter((d) => d.tier === t) }));

  const done = DIRECTORIES.filter((d) => ["submitted", "verified"].includes(statuses[d.slug]?.status)).length;
  const verified = DIRECTORIES.filter((d) => statuses[d.slug]?.status === "verified").length;

  return (
    <div className="mt-8" data-testid="directories-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-1">Directory blast</div>
          <h2 className="font-display text-2xl leading-tight">Get RIZ N WASH scraped into LLM training data.</h2>
          <p className="text-sm text-rw-ink2 mt-2 max-w-2xl">
            LLMs learn about local businesses from directories. The more you're listed, the more likely
            ChatGPT / Claude / Gemini mention you when someone asks "best laundry in Gurugram".
          </p>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl tabular-nums">{done} <span className="text-base text-rw-ink3">/ {DIRECTORIES.length}</span></div>
          <div className="text-xs text-rw-ink3">submitted · {verified} verified</div>
        </div>
      </div>

      {/* Listing kit */}
      {kit && (
        <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 mb-6" data-testid="listing-kit">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Copy-ready listing kit</div>
              <div className="text-sm text-rw-ink2">Paste these into each directory's form — same fields everywhere.</div>
            </div>
            <button onClick={copyAll} className="btn-dark rounded-full px-4 py-2 text-xs font-semibold" data-testid="copy-all-btn">
              Copy all →
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {[
              ["Business name", kit.business_name],
              ["Phone", kit.phone],
              ["Address", kit.address_line],
              ["Website", kit.website],
              ["Categories", kit.categories.join(", ")],
              ["Hours", kit.hours],
              ["Short description", kit.short_description],
              ["Long description", kit.long_description],
            ].map(([label, val]) => (
              <div key={label} className="rounded-xl border border-rw-ink/10 bg-rw-bg p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="overline text-rw-ink3">{label}</div>
                  <button onClick={() => copy(val, label.toLowerCase())} className="text-xs text-rw-blue hover:underline">
                    Copy
                  </button>
                </div>
                <div className="mt-1 text-rw-ink2 break-words">{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-rw-ink3 text-sm">Loading…</div>}

      {/* Directory cards grouped by tier */}
      {groups.map(({ tier, items }) => (
        <section key={tier} className="mb-8">
          <div className="overline mb-3">{tier}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((d) => {
              const s = statuses[d.slug] || { status: "todo" };
              return (
                <div key={d.slug} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 flex flex-col gap-3" data-testid={`dir-card-${d.slug}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div>
                      <div className="font-display text-lg leading-tight">{d.name}</div>
                      <div className="text-xs text-rw-ink3 mt-1">~{d.typicalTimeMin} min</div>
                    </div>
                    <StatusPill status={s.status || "todo"} />
                  </div>
                  <p className="text-xs text-rw-ink2 leading-relaxed flex-1">{d.notes}</p>
                  <div className="flex flex-wrap gap-2">
                    <motion.a
                      whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                      href={d.submitUrl} target="_blank" rel="noopener noreferrer"
                      onClick={() => { if (s.status === "todo" || !s.status) updateStatus(d.slug, { status: "submitted" }); }}
                      className="btn-dark rounded-full px-4 py-1.5 text-xs font-semibold"
                      data-testid={`dir-open-${d.slug}`}
                    >
                      Open form →
                    </motion.a>
                    <select
                      value={s.status || "todo"}
                      onChange={(e) => updateStatus(d.slug, { status: e.target.value })}
                      className="rounded-full border border-rw-ink/15 px-3 py-1.5 text-xs font-semibold bg-white focus:outline-none focus:border-rw-blue"
                      data-testid={`dir-status-${d.slug}`}
                    >
                      <option value="todo">To do</option>
                      <option value="submitted">Submitted</option>
                      <option value="verified">Verified</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  {s.listing_url && (
                    <a href={s.listing_url} target="_blank" rel="noopener noreferrer" className="text-xs text-rw-blue hover:underline break-all">
                      {s.listing_url}
                    </a>
                  )}
                  <input
                    type="text"
                    defaultValue={s.listing_url || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (s.listing_url || "")) {
                        updateStatus(d.slug, { listing_url: e.target.value });
                      }
                    }}
                    placeholder="Paste live listing URL (after verify)"
                    className="rounded-lg border border-rw-ink/10 px-3 py-1.5 text-xs bg-white focus:outline-none focus:border-rw-blue"
                    data-testid={`dir-url-${d.slug}`}
                  />
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
